import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { format, subMonths, add } from 'date-fns'
import { FormattedMessage } from 'react-intl'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { toFixedCustom } from 'utils'
import { getPerformanceChartData, getNavChartData, getAnnualReturnData, getCumulativeReturnData, isCompositionNAV } from '../../services'
import OriginPerformanceTab from '../../../../organisms/FundDetailPerformanceTab'

import './style.scss'

const FundDetailPerformanceTab = ({ tableDisclaimer, shareClassId, taCode, isYoungFund, pastPerformanceDisclaimer }) => {
  const [highchartToggleConfig, setHighchartToggleConfig] = useState()
  const [highchartOptions, setHighchartOptions] = useState()
  const [tableDataArray, setTableDataArray] = useState([])
  const [apiHasError, setApiHasError] = useState(false)

  const intl = getCustomIntl()
  const { countryConfig } = getGlobalParams()

  const [downloadOptions, setDownloadOptions] = useState({
    label: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.nav.downloadLinkContent' }),
    url: ''
  })

  const tooltipFormatter = (pointThis, htmlFormatter) => {
    const formattedTooltipHtml = pointThis.points.map((point) => htmlFormatter(point))
    return [``].concat(formattedTooltipHtml)
  }

  const getTables = async () => {
    // Tables
    const getAnnualReturnTableDate = async () => {
      const annualReturnData = !isYoungFund
        ? (await getAnnualReturnData(shareClassId)) || {
            hasError: true
          }
        : null
      return {
        id: `fil-fund-detail-${shareClassId}__annual-return-table`,
        title: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.annualReturnTable.title' }),
        tableData: annualReturnData,
        extraClasses: 'fil-fund-detail__tab-performance__table-annual'
      }
    }

    const getCumulativeReturnTableData = async () => {
      const cumulativeReturnData = !isYoungFund
        ? (await getCumulativeReturnData(shareClassId)) || {
            tableData: { hasError: true }
          }
        : null
      return {
        id: `fil-fund-detail-${shareClassId}__cumulative-return-table`,
        title: (
          <div className="fil-fund-detail__tab-performance__table-cumulative-title">
            <span>
              <FormattedMessage id="fundDetail.tabs.performanceTab.cumulativeReturn.title" />
            </span>
            <span className="text-body-m fil-fund-detail__tab-performance__table-cumulative-title-date">
              {cumulativeReturnData.tableData.hasError ? '' : `(${cumulativeReturnData.returnDate})`}
            </span>
          </div>
        ),
        extraClasses: 'fil-fund-detail__tab-performance__table-cumulative',
        tableData: cumulativeReturnData.tableData,
        tableDisclaimer
      }
    }

    let tablesData = []
    if (!isYoungFund) {
      const annualReturnTablePromise = getAnnualReturnTableDate()
      const cumulativeReturnTablePromise = getCumulativeReturnTableData()

      const [annualReturnTable, cumulativeReturnTable] = await Promise.all([annualReturnTablePromise, cumulativeReturnTablePromise])

      tablesData = [
        ...(annualReturnTable.tableData ? [annualReturnTable] : []),
        ...(cumulativeReturnTable.tableData ? [cumulativeReturnTable] : [])
      ]
    }

    setTableDataArray(tablesData)
  }

  // Download Excel
  const getDownloadOptions = async () => {
    const isNewNavTemplate = countryConfig.api4NAVTpl
      ? await isCompositionNAV(shareClassId)
      : countryConfig.newNavTemplateCodes.includes(taCode)

    setDownloadOptions({
      ...downloadOptions,
      url: `/xapi/fundPrice/excel?scId=${shareClassId}${isNewNavTemplate ? '&hasCompositionNavReport=true' : ''}`
    })
  }

  const getChartsInfo = async () => {
    // Charts
    const performanceDataPromise = !isYoungFund ? getPerformanceChartData(shareClassId) : null
    const navDataPromise = getNavChartData(shareClassId)

    const [performanceData, navData] = await Promise.all([performanceDataPromise, navDataPromise])
    const hasError = (performanceData && performanceData.hasError) || navData.hasError
    setApiHasError(hasError)

    const rangeSelectorButtonsContent = {
      ytd: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.YTD' }),
      '3m': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.monthsText' }, { monthsCount: 3 }),
      '6m': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.monthsText' }, { monthsCount: 6 }),
      '1y': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 1 }),
      '2y': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 2 }),
      '3y': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 3 }),
      '5y': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 5 }),
      '10y': intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 10 }),
      all: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.sinceLaunch' })
    }

    const highchartDatePickerConfig = {
      labels: [
        intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.highchartStartDateLabel' }),
        intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.highchartEndDateLabel' })
      ],
      dateFormatLabel: 'YYYY-MM-DD',
      locale: 'zh_tw'
    }

    const formattedHighchartOptions = {
      ...(performanceData && !hasError
        ? {
            performance: {
              options: {
                plotOptions: {
                  // Calculate percentage automatically
                  series: {
                    compare: 'percent',
                    compareStart: true
                  }
                },
                legend: {
                  x: 57
                },
                tooltip: {
                  changeDecimals: 2,
                  valueDecimals: 2,
                  formatter() {
                    const self = this
                    const getFormattedHtml = (point) => `<div>
                    <div>${format(point.x, 'yyyy/MM/dd')}</div>
                    <div>${point.series.name}: ${toFixedCustom(point.point.change, 2)}%</div>
                  </div>`
                    return tooltipFormatter(self, getFormattedHtml)
                  }
                },
                displayMinMax: false,
                startFromZero: true,
                // dataDurationType: 'monthly',
                topOptions: {
                  lang: {
                    rangeSelectorZoom: ''
                  }
                },
                credits: {
                  enabled: false
                },
                rangeSelectorOptions: {
                  rangeSelectorButtonsContent,
                  selectedId: 'all',
                  onClick: () => {}
                },
                xAxis: {
                  labels: {
                    formatter(xLabelFormatter) {
                      const self = this
                      if (format(add(subMonths(self.axis.max, 6), { days: 1 }), 'TT') > self.axis.min) {
                        return format(xLabelFormatter.value, 'yyyy/MM')
                      }
                      return format(xLabelFormatter.value, 'MM/dd')
                    }
                  }
                },
                yAxis: {
                  labels: {
                    formatter(yLabelFormatter) {
                      const isDecimalNumber = Math.abs(yLabelFormatter.value) > 0 && Math.abs(yLabelFormatter.value) < 1
                      return `${parseFloat(yLabelFormatter.value).toFixed(isDecimalNumber ? 2 : 1)} %`
                    }
                  }
                },
                updateDate: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.updateDate' }, { updateDate: performanceData.date }),
                ...performanceData
              },
              commonOptions: {
                highchartDatePickerConfig
              }
            }
          }
        : {}),
      ...(!hasError
        ? {
            nav: {
              options: {
                legend: {
                  x: 39
                },
                tooltip: {
                  formatter() {
                    const self = this
                    const getFormattedHtml = (point) => `<div>
                <div>${format(point.x, 'yyyy/MM/dd')}</div>
                <div>${point.series.name}: ${toFixedCustom(point.y, 4)}</div>
              </div>
            `

                    return tooltipFormatter(self, getFormattedHtml)
                  }
                },
                displayMinMax: false,
                topOptions: {
                  lang: {
                    rangeSelectorFrom: '由',
                    rangeSelectorTo: '到',
                    rangeSelectorZoom: ''
                  }
                },
                credits: {
                  enabled: false
                },
                rangeSelectorOptions: {
                  rangeSelectorButtonsContent,
                  selectedId: 'all'
                },
                yAxis: {
                  labels: {
                    formatter(yLabelFormatter) {
                      return toFixedCustom(yLabelFormatter.value, 2)
                    }
                  }
                },
                updateDate: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.updateDate' }, { updateDate: navData.date }),
                ...navData
              },
              commonOptions: {
                highchartDatePickerConfig
              }
            }
          }
        : {})
    }

    const performanceToggleContent = intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.performance.toggleContent' })
    const navToggleContent = intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.nav.toggleContent' })
    const formattedHighchartToggleConfig = {
      id: 'fil-fund-detail__performance-tab-toggle',
      selectedChart: performanceData ? 'performance' : 'nav',
      highchartToggleContent: [
        {
          id: 'performance',
          name: 'highchart-toggle',
          value: performanceToggleContent,
          label: performanceToggleContent,
          description: performanceToggleContent,
          ...(!performanceData || hasError ? { disabled: true } : {})
        },
        {
          id: 'nav',
          name: 'highchart-toggle',
          value: navToggleContent,
          label: navToggleContent,
          description: navToggleContent,
          ...(hasError ? { disabled: true } : {})
        }
      ]
    }

    setHighchartOptions(formattedHighchartOptions)
    setHighchartToggleConfig(formattedHighchartToggleConfig)
  }

  useEffect(() => {
    getTables()
    getChartsInfo()
    getDownloadOptions()
  }, [])

  return (
    <OriginPerformanceTab
      {...{
        highchartToggleConfig,
        highchartOptions,
        downloadOptions,
        tableDataArray,
        isYoungFund,
        apiHasError,
        pastPerformanceDisclaimer
      }}
    />
  )
}

FundDetailPerformanceTab.propTypes = {
  shareClassId: PropTypes.string.isRequired,
  taCode: PropTypes.string.isRequired,
  tableDisclaimer: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isYoungFund: PropTypes.bool,
  pastPerformanceDisclaimer: PropTypes.string
}

FundDetailPerformanceTab.defaultProps = {
  tableDisclaimer: '',
  isYoungFund: false,
  pastPerformanceDisclaimer: ''
}

export default FundDetailPerformanceTab
